import { ISidebar } from "../../types/ISidebar";
import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { IoIosReturnRight } from "react-icons/io";
import { MdClose } from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import { useSidebarStore } from "../Layout/Store";
import CompanySwitcher from "../CompanySwitcher";
import S from "./styles.module.scss";
import { useLoggedUserStore, useSettingsStore } from "@/Store";

const Sidebar = ({ sidebarData }: ISidebar) => {
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const settings = useSettingsStore((state) => state.settings);
  const location = useLocation();

  const { isSidebarOpen, handleOpenSidebar, closeSidebar } = useSidebarStore();
  const sidebarRef = useRef(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
      closeSidebar();
    }
  };

  const [sidebar, setSidebar] = useState(sidebarData);

  useEffect(() => {
    const updatedSidebar = sidebarData.map((item) => {
      if (item.submenu) {
        const updatedSubmenu = item.submenu.map((subitem) => {
          if (location.pathname === subitem.path) {
            return { ...subitem, active: true };
          } else {
            return { ...subitem, active: false };
          }
        });

        const isSubitemActive = updatedSubmenu.some(
          (subitem) => subitem.active
        );
        return { ...item, active: isSubitemActive, submenu: updatedSubmenu };
      } else {
        if (location.pathname === item.path) {
          return { ...item, active: true };
        } else {
          return { ...item, active: false };
        }
      }
    });
    setSidebar(updatedSidebar);

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [location.pathname, sidebarData]);

  const renderItem = (item: ISidebar["sidebarData"][number], key: string) =>
    !item.submenu ? (
      <li
        key={key}
        className={`${S["sidebar-list-item"]} ${item.active ? S.active : ""}`}
      >
        <Link
          className={S["sidebar-item"]}
          to={(item.path ?? "#").toString()}
          onClick={closeSidebar}
        >
          {typeof item.icon === "function"
            ? item.icon({ size: 20 })
            : React.createElement(item.icon, { size: 20 })}
          {React.createElement(React.Fragment, null, String(item.label))}
        </Link>
      </li>
    ) : (
      <li
        key={key}
        className={`${S["sidebar-list-item"]} ${item.active ? S.active : ""}`}
      >
        <span
          onClick={() => {
            const checkbox = document.getElementById(key) as HTMLInputElement;
            if (checkbox) checkbox.checked = !checkbox.checked;
            const checkboxC = document.getElementById(
              `${key}-c`
            ) as HTMLInputElement;
            if (checkboxC) checkboxC.checked = !checkboxC.checked;
          }}
          className={`${S["sidebar-item"]} ${item.active ? S.active : ""}`}
        >
          {typeof item.icon === "function"
            ? item.icon({ size: 20 })
            : React.createElement(item.icon, { size: 20 })}
          {React.createElement(React.Fragment, null, String(item.label))}
          <input
            type="checkbox"
            id={`${key}-c`}
            defaultChecked={!!item.active}
          />
          <FaChevronDown className={S.ChevronDown} />
        </span>
        <input type="checkbox" id={key} defaultChecked={!!item.active} />
        <ul className={S["submenu"]}>
          {item.submenu.map((subitem: any, subIndex: number) => (
            <li key={subIndex}>
              <Link
                to={subitem.path}
                onClick={closeSidebar}
                className={`${subitem.active ? S.active : ""}`}
              >
                <IoIosReturnRight />
                {subitem.label}
              </Link>
            </li>
          ))}
        </ul>
      </li>
    );

  const isPlatformStaff =
    loggedUser?.type === "superadmin" || loggedUser?.type === "support";
  const isAdmin = ["superadmin", "owner", "admin"].includes(
    loggedUser?.type ?? ""
  );
  const userModules = loggedUser?.modules ?? [];
  const visible = sidebar.filter((s) => {
    if ("ownerOnly" in s && s.ownerOnly) return loggedUser?.type === "owner";
    if ("accountOwnerOrStaff" in s && s.accountOwnerOrStaff)
      return loggedUser?.type === "owner" || isPlatformStaff;
    if (s.superAdmin) return isPlatformStaff;
    if (isAdmin) return true;
    // member: item de feature exige o módulo liberado; itens gerais (Comum) sempre.
    if (s.module) return userModules.includes(s.module);
    return s.permission?.includes("Comum");
  });
  const geralItems = visible.filter((s) => s.context === "geral");
  const empresaItems = visible.filter((s) => s.context !== "geral");

  return (
    <aside ref={sidebarRef} className={S[isSidebarOpen ? "open" : ""]}>
      <div className={S["sidebar-header"]}>
        <div className={S.logo}>
          <span className={S.logoMark}>
            {(settings?.displayName || "Genialogic").charAt(0).toUpperCase()}
          </span>
          <h2 className={S["sidebar-title"]}>
            {settings?.displayName || "Genialogic"}
          </h2>
        </div>
        <MdClose
          className={S["close-icon"]}
          size={24}
          onClick={handleOpenSidebar}
        />
      </div>
      {geralItems.length > 0 && (
        <ul className={S["sidebar-list"]}>
          <span className={S["list-title"]}>Geral</span>
          {geralItems.map((item, index) => renderItem(item, `g-${index}`))}
        </ul>
      )}
      {empresaItems.length > 0 && (
        <div className={S["company-section"]}>
          <span className={S["section-eyebrow"]}>Empresa ativa</span>
          <div className={S["switcher-wrap"]}>
            <CompanySwitcher variant="sidebar" />
          </div>
          <ul className={S["sidebar-list"]}>
            {empresaItems.map((item, index) => renderItem(item, `e-${index}`))}
          </ul>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
